import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { ReviewPopup } from '../ReviewSystem/ReviewPopup'
import { WatchlistCardView } from './UserWatchlist/WatchlistCardView'
import { WatchlistListView } from './UserWatchlist/WatchlistListView'
import { AnimeWatchlistEdit } from './UserWatchlist/AnimeWatchlistEdit'
import { EmptyWatchlist } from './UserWatchlist/EmptyWatchlist'
import { useClickOutside } from '../../hooks/useClickOutside'
import { CardIcon, GhostIcon, ListIcon } from '../ui/icons/user'
import { PlusIcon } from '../ui/icons/shared'

/* כמה פריטים מוסיפים בכל Batch */
const BATCH_SIZE = 36

export function StatusSection({
  /* meta */
  statusTitle,
  statusValue,
  totalCount = 0, // 💡 מגיע מ-parent – סה"כ רשומות ב-DB
  /* data */
  items = [],
  fullListLength,
  /* handlers */
  onIncrement,
  onDecrement,
  onAdd,
  onRemove,
  onSaveChanges,
  getUpdatedWatchItem,
  canEdit,
}) {
  /* ─────────── UI state ─────────── */
  const [viewMode, setViewMode] = useState('cards') // 'cards' | 'list'
  const [visibleCnt, setVisibleCnt] = useState(BATCH_SIZE)

  /* modals */
  const [editId, setEditId] = useState(null)
  const [editData, setEditData] = useState({})
  const [rvOpen, setRvOpen] = useState(false)
  const [rvId, setRvId] = useState(null)
  const [rvInit, setRvInit] = useState(null)

  /* redux */
  const loggedUser = useSelector((s) => s.userModule.loggedInUser)
  const reviews = useSelector((s) => s.reviewModule.reviews) ?? []

  /* sort hebrew once (perf) */
  const sorted = useMemo(() => {
    const arr = [...items]
    arr.sort((a, b) =>
      (a.title_hebrew || '').localeCompare(b.title_hebrew || '', 'he')
    )
    return arr
  }, [items])

  /* infinite-observer (local batches) */
  const sentinelRef = useRef(null)

  useEffect(() => {
    setVisibleCnt(BATCH_SIZE)
  }, [items])

  useEffect(() => {
    if (!sentinelRef.current) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting)
          setVisibleCnt((c) => Math.min(c + BATCH_SIZE, sorted.length))
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    )
    io.observe(sentinelRef.current)
    return () => io.disconnect()
  }, [sorted.length])

  /* helpers */
  const userReviewed = useCallback(
    (id) =>
      reviews.find((r) => r.animeId === id && r.userId === loggedUser?._id),
    [reviews, loggedUser]
  )

  const openEdit = (itm) => {
    if (!canEdit) return
    setEditId(itm.anizone_id)
    setEditData({ progress: itm.progress, status: itm.status })
  }
  const closeEdit = () => {
    setEditId(null)
    setEditData({})
  }

  const saveEdit = (itm) => {
    if (!canEdit) return toast.error('אין הרשאה')
    onSaveChanges(getUpdatedWatchItem({ ...itm, ...editData }))
    closeEdit()
  }

  const openReview = (id) => {
    if (!loggedUser) return toast.error('עליך להתחבר כדי לדרג')
    setRvInit(userReviewed(id) || null)
    setRvId(id)
    setRvOpen(true)
  }

  /* click-outside (modal) */
  useClickOutside(sentinelRef, () => {}, false)

  /* empty Plan-to-watch when whole list empty */
  if (fullListLength === 0 && canEdit && statusValue === 'Plan to Watch') {
    return (
      <div className="mb-12 mt-6">
        <EmptyWatchlist onAdd={onAdd} statusValue={statusValue} />
      </div>
    )
  }

  /* UX messages */
  const emptyTxt = {
    Watching: 'אין אנימות במעקב כרגע – התחל לצפות כדי להוסיף.',
    'Plan to Watch': 'הוסף אנימה שאתה מתכנן לצפות בה.',
    Completed: 'כשתשלים סדרה היא תופיע כאן.',
    'On-Hold': 'סדרות שהוקפאו יוצגו כאן.',
    Dropped: 'סדרות שננטשו יוצגו כאן.',
  }[statusValue]

  const visibleItems = sorted.slice(0, visibleCnt)

  /* ─────────── JSX ─────────── */
  return (
    <section className="mb-12" aria-labelledby={`heading-${statusValue}`}>
      {/* ░░ Header ░░ */}
      <header className="flex justify-between mb-4">
        <h2
          id={`heading-${statusValue}`}
          className="flex gap-2 items-center text-xl text-white"
        >
          <span>{statusTitle}</span>
          <span
            className="text-primary"
            aria-label={`סה״כ ${totalCount} אנימות בסטטוס ${statusTitle}`}
            title={`סה״כ ${totalCount}`}
          >
            {totalCount}
          </span>
        </h2>

        {/* actions */}
        <div className="flex gap-5 items-center">
          {canEdit && (
            <button
              type="button"
              className="flex gap-2 items-center text-white"
              onClick={() => {
                onAdd(statusValue)
                window.scrollTo({ top: 600, behavior: 'smooth' })
              }}
              aria-label={`הוסף אנימה ל-${statusTitle}`}
              title={`הוסף ל-${statusTitle}`}
            >
              <div className="w-6 h-6 bg-accent rounded-full flex justify-center items-center">
                <PlusIcon />
              </div>
              הוספה
            </button>
          )}

          {/* view-toggle (DRY: small helper) */}
          {[
            { mode: 'list', Icon: ListIcon, label: 'רשימה' },
            { mode: 'cards', Icon: CardIcon, label: 'כרטיסים' },
          ].map(({ mode, Icon, label }) => (
            <button
              key={mode}
              type="button"
              aria-pressed={viewMode === mode}
              aria-label={`תצוגת ${label}`}
              title={`תצוגת ${label}`}
              onClick={() => setViewMode(mode)}
            >
              <Icon
                className={viewMode === mode ? 'fill-primary' : 'fill-muted'}
              />
            </button>
          ))}
        </div>
      </header>

      {/* ░░ Content ░░ */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-4 mt-6">
          <GhostIcon aria-hidden="true" />
          <p className="text-softText text-center">{emptyTxt}</p>
        </div>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <WatchlistCardView
              items={visibleItems}
              canEdit={canEdit}
              onIncrement={onIncrement}
              onDecrement={onDecrement}
              onRemove={onRemove}
              onOpenEdit={openEdit}
            />
          ) : (
            <WatchlistListView
              items={visibleItems}
              canEdit={canEdit}
              onIncrement={onIncrement}
              onDecrement={onDecrement}
              onRemove={onRemove}
              onOpenEdit={openEdit}
            />
          )}

          {/* sentinel – מגדיל batch ב-observer */}
          {visibleCnt < sorted.length && (
            <div ref={sentinelRef} className="h-6" aria-hidden="true" />
          )}
        </>
      )}

      {/* ░░ Edit-modal ░░ */}
      {canEdit &&
        editId &&
        (() => {
          const itm = items.find((i) => i.anizone_id === editId)
          if (!itm) return null
          return (
            <AnimeWatchlistEdit
              item={itm}
              editData={editData}
              setEditData={setEditData}
              handleSaveItemChanges={saveEdit}
              getUpdatedWatchItem={getUpdatedWatchItem}
              handleCloseEdit={closeEdit}
              handleOpenReviewPopup={openReview}
              userAlreadyReviewed={userReviewed}
              onRemove={onRemove}
            />
          )
        })()}

      {/* ░░ Review-popup ░░ */}
      {rvOpen && rvId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <ReviewPopup
            initialData={rvInit}
            animeId={rvId}
            loggedInUser={loggedUser}
            onClose={() => setRvOpen(false)}
          />
        </div>
      )}
    </section>
  )
}
